import moment from 'moment';
import { XHRRequestCanceler } from '../../../../Libs/xhr/XHRRequestCanceler';
import {
    IPlanningDataService,
    PlanningDataServiceName,
    PlanningEndpoints,
} from '../../../DataServices/PlanningDataService';
import { Lazy } from '../../../Helpers/Lazy';
import { BaseApiDataResponse } from '../../../Models/ApiData';
import { AffectationResponse } from '../../../Models/Planning/Affectation';
import { PlanningResponse } from '../../../Models/Planning/Planning';
import { SetPlanning } from '../../../Models/Planning/SetPlanning';
import { ExportFileType, ExportUtils } from '../../../Utils/ExportUtils';
import { IoC } from '../../ServicesContainer';
import { BaseReduxService } from '../Api/BaseReduxService';
import { AuthServiceName, IAuthService } from '../Authentication/AuthenticationService';
import { DownloadServiceName, IDownloadService } from '../Download/DownloadService';

export interface IPlanningService {
    getPlanning: (
        cts: XHRRequestCanceler,
        start_date: Date,
        end_date: Date
    ) => Promise<PlanningResponse>;

    setPlanning: (
        cts: XHRRequestCanceler,
        setPlanning: SetPlanning
    ) => Promise<BaseApiDataResponse>;

    getAffectations: (cts: XHRRequestCanceler) => Promise<AffectationResponse>;

    publishPlanning: (
        cts: XHRRequestCanceler,
        week_start_date: Date
    ) => Promise<BaseApiDataResponse>;

    exportPlanning: (cts: XHRRequestCanceler, start_date: Date, end_date: Date) => void;
}

class PlanningService extends BaseReduxService implements IPlanningService {
    private readonly planningDataService: Lazy<IPlanningDataService>;
    private readonly authService: Lazy<IAuthService>;
    private readonly downloadService: Lazy<IDownloadService>;

    constructor() {
        super();
        this.planningDataService = IoC.getLazy<IPlanningDataService>(PlanningDataServiceName);
        this.authService = IoC.getLazy<IAuthService>(AuthServiceName);
        this.downloadService = IoC.getLazy<IDownloadService>(DownloadServiceName);
    }

    public publishPlanning = async (
        cts: XHRRequestCanceler,
        week_start_date: Date
    ): Promise<BaseApiDataResponse> => {
        try {
            return await this.planningDataService.value().publishPlanning(cts, week_start_date);
        } catch (err) {
            return Promise.reject(err);
        }
    };

    public setPlanning = (
        cts: XHRRequestCanceler,
        setPlanning: SetPlanning
    ): Promise<BaseApiDataResponse> => {
        try {
            return this.planningDataService.value().setPlanning(cts, setPlanning);
        } catch (err) {
            return Promise.reject(err);
        }
    };

    public getPlanning = async (
        cts: XHRRequestCanceler,
        start_date: Date,
        end_date: Date
    ): Promise<PlanningResponse> => {
        try {
            const response = await this.planningDataService
                .value()
                .getPlanning(cts, start_date, end_date);
            return Promise.resolve(response);
        } catch (err) {
            return Promise.reject(err);
        }
    };

    public getAffectations = async (cts: XHRRequestCanceler): Promise<AffectationResponse> => {
        try {
            const response = await this.planningDataService.value().getAffectations(cts);
            return Promise.resolve(response);
        } catch (err) {
            return Promise.reject(err);
        }
    };

    public exportPlanning = async (
        cts: XHRRequestCanceler,
        start_date: Date,
        end_date: Date
    ): Promise<void> => {
        try {
            const params: { [key: string]: string } = {};
            params['start_date'] = encodeURIComponent(moment(start_date).format());
            params['end_date'] = encodeURIComponent(moment(end_date).format());

            const url: string = ExportUtils.getExportUrlWithParams(
                {
                    url: PlanningEndpoints.EXPORT,
                    auth: await this.authService.value().getAuthorization(),
                },
                params
            );
            this.downloadService.value().downloadFile(url, 'Planning', ExportFileType.EXCEL, cts);
        } catch (err) {
            Promise.reject(err);
        }
    };
}

const PlanningServiceName = 'PlanningService';
export { PlanningService, PlanningServiceName };
